import {
    SUBSCRIPTION_BLOCK_CODES,
    SUBSCRIPTION_EXPIRED_STORAGE_KEY,
    RENEWAL_CHECKOUT_STORAGE_KEY,
    RENEW_PATH_PREFIXES,
} from 'constants/subscription.constant'
import store from '../store'

export function normalizeApiField(obj, ...keys) {
    if (!obj) return undefined
    for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) {
            return obj[key]
        }
    }
    return undefined
}

export function getApiCode(body) {
    return normalizeApiField(body, 'Code', 'code') || ''
}

export function getApiStatus(body) {
    return normalizeApiField(body, 'Status', 'status')
}

export function getApiMessage(body) {
    return normalizeApiField(body, 'Message', 'message') || ''
}

export function getApiData(body) {
    return normalizeApiField(body, 'Data', 'data') || null
}

export function isSubscriptionBlockCode(code) {
    if (!code) return false
    const normalized = String(code)
    if (SUBSCRIPTION_BLOCK_CODES.includes(normalized)) return true
    return normalized.startsWith('SUBSCRIPTION')
}

export function isSubscriptionBlockedResponse(httpStatus, body) {
    if (httpStatus !== 403) return false
    return isSubscriptionBlockCode(getApiCode(body))
}

export function buildSubscriptionExpiredPayload(body, extras = {}) {
    const data = getApiData(body) || {}

    return {
        code: getApiCode(body),
        message: getApiMessage(body),
        companyId: data.CompanyId ?? data.companyId ?? extras.companyId ?? null,
        subscriptionStatus: data.SubscriptionStatus ?? data.subscriptionStatus ?? null,
        planName: data.PlanName ?? data.planName ?? null,
        currentPeriodEnd: data.CurrentPeriodEnd ?? data.currentPeriodEnd ?? null,
        isTrialAccount: data.IsTrialAccount ?? data.isTrialAccount ?? false,
        trialEndsAt: data.TrialEndsAt ?? data.trialEndsAt ?? null,
        emailAddress: extras.emailAddress ?? data.EmailAddress ?? data.emailAddress ?? data.email ?? null,
        ...extras,
    }
}

export function persistSubscriptionExpired(body, extras = {}) {
    try {
        sessionStorage.setItem(
            SUBSCRIPTION_EXPIRED_STORAGE_KEY,
            JSON.stringify(buildSubscriptionExpiredPayload(body, extras))
        )
    } catch (_) {
        // ignore storage errors
    }
}

export function readSubscriptionExpired() {
    try {
        const raw = sessionStorage.getItem(SUBSCRIPTION_EXPIRED_STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch (_) {
        return null
    }
}

export function clearSubscriptionExpired() {
    try {
        sessionStorage.removeItem(SUBSCRIPTION_EXPIRED_STORAGE_KEY)
    } catch (_) {
        // ignore storage errors
    }
}

export function persistRenewalCheckout(details) {
    try {
        sessionStorage.setItem(RENEWAL_CHECKOUT_STORAGE_KEY, JSON.stringify(details))
    } catch (_) {
        // ignore storage errors
    }
}

export function readRenewalCheckout() {
    try {
        const raw = sessionStorage.getItem(RENEWAL_CHECKOUT_STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch (_) {
        return null
    }
}

export function clearRenewalCheckout() {
    try {
        sessionStorage.removeItem(RENEWAL_CHECKOUT_STORAGE_KEY)
    } catch (_) {
        // ignore storage errors
    }
}

export function isPlatformAdmin() {
    const authority = store.getState()?.auth?.user?.authority || []
    return authority.includes('admin')
}

export function shouldRedirectToRenew() {
    // Platform admins review customer companies; never send them to manager billing.
    if (isPlatformAdmin()) {
        return false
    }

    const path = window.location.pathname
    return !RENEW_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))
}

export function redirectToRenew() {
    const token = store.getState()?.auth?.session?.token
    if (token) {
        window.location.assign('/billing?renew=1')
        return
    }
    window.location.assign('/renew-subscription')
}

export function handleSubscriptionBlockedApiResponse(httpStatus, body) {
    if (!isSubscriptionBlockedResponse(httpStatus, body)) {
        return false
    }

    // Platform admins manage customer accounts; 403s on customer APIs are not their subscription.
    if (isPlatformAdmin()) {
        return true
    }

    persistSubscriptionExpired(body)

    if (shouldRedirectToRenew()) {
        redirectToRenew()
    }

    return true
}
